import React from 'react';
import { RefreshCw, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { CommentFetchStatus } from '../../lib/types';
interface CommentsProgressProps {
  progress: CommentFetchStatus;
}
export function CommentsProgress({
  progress
}: CommentsProgressProps) {
  const getStatusColor = () => {
    switch (progress.status) {
      case 'in_progress':
        return 'text-blue-600 dark:text-blue-400';
      case 'completed':
        return 'text-green-600 dark:text-green-400';
      case 'failed':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-muted-foreground';
    }
  };
  const getStatusIcon = () => {
    switch (progress.status) {
      case 'in_progress':
        return <RefreshCw size={16} className="animate-spin" data-id="element-1230" />;
      case 'completed':
        return <CheckCircle size={16} data-id="element-1231" />;
      case 'failed':
        return <AlertCircle size={16} data-id="element-1232" />;
      default:
        return null;
    }
  };
  return <div className="border border-primary/30 rounded-lg bg-card p-4" data-id="element-1233">
      {/* Header */}
      <div className="flex items-center justify-between mb-4" data-id="element-1234">
        <h4 className="font-medium text-sm flex items-center gap-2" data-id="element-1235">
          <span className={getStatusColor()} data-id="element-1236">{getStatusIcon()}</span>
          {progress.status === 'in_progress' && 'Fetching Comments...'}
          {progress.status === 'completed' && 'Fetch Complete!'}
          {progress.status === 'failed' && 'Fetch Failed'}
        </h4>
        {progress.provider && <span className="text-xs text-muted-foreground" data-id="element-1237">
            via {progress.provider}
          </span>}
      </div>

      {/* Progress Bar */}
      <div className="mb-4" data-id="element-1238">
        <div className="flex items-center justify-between mb-2" data-id="element-1239">
          <span className="text-xs text-muted-foreground" data-id="element-1240">Progress</span>
          <span className="text-xs font-medium" data-id="element-1241">
            {progress.progress_percent.toFixed(1)}%
          </span>
        </div>
        <div className="h-2 bg-secondary rounded-full overflow-hidden" data-id="element-1242">
          <div className={`h-full transition-all duration-500 ${progress.status === 'completed' ? 'bg-green-500' : progress.status === 'failed' ? 'bg-red-500' : 'bg-primary'}`} style={{
          width: `${progress.progress_percent}%`
        }} data-id="element-1243" />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-id="element-1244">
        <div className="bg-secondary/30 rounded-lg p-3" data-id="element-1245">
          <div className="text-xs text-muted-foreground mb-1" data-id="element-1246">Fetched</div>
          <div className="text-lg font-semibold" data-id="element-1247">
            {progress.comments_fetched.toLocaleString()}
          </div>
        </div>
        <div className="bg-secondary/30 rounded-lg p-3" data-id="element-1248">
          <div className="text-xs text-muted-foreground mb-1" data-id="element-1249">Estimated</div>
          <div className="text-lg font-semibold" data-id="element-1250">
            {progress.estimated_total.toLocaleString()}
          </div>
        </div>
        {progress.current_page && progress.total_pages_estimate && <div className="bg-secondary/30 rounded-lg p-3" data-id="element-1251">
            <div className="text-xs text-muted-foreground mb-1" data-id="element-1252">Page</div>
            <div className="text-lg font-semibold" data-id="element-1253">
              {progress.current_page}/{progress.total_pages_estimate}
            </div>
          </div>}
        {progress.eta_seconds && progress.status === 'in_progress' && <div className="bg-secondary/30 rounded-lg p-3" data-id="element-1254">
            <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1" data-id="element-1255">
              <Clock size={10} data-id="element-1256" />
              ETA
            </div>
            <div className="text-lg font-semibold" data-id="element-1257">{progress.eta_seconds}s</div>
          </div>}
        {progress.credits_charged !== undefined && <div className="bg-secondary/30 rounded-lg p-3" data-id="element-1258">
            <div className="text-xs text-muted-foreground mb-1" data-id="element-1259">Credits</div>
            <div className="text-lg font-semibold text-primary" data-id="element-1260">
              {progress.credits_charged}
            </div>
          </div>}
      </div>

      {/* Error Message */}
      {progress.status === 'failed' && progress.error_message && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg" data-id="element-1261">
          <div className="flex items-start gap-2" data-id="element-1262">
            <AlertCircle size={16} className="text-destructive mt-0.5 flex-shrink-0" data-id="element-1263" />
            <div className="text-xs text-destructive" data-id="element-1264">
              <p className="font-medium mb-1" data-id="element-1265">Error occurred:</p>
              <p data-id="element-1266">{progress.error_message}</p>
            </div>
          </div>
        </div>}

      {/* Success Message */}
      {progress.status === 'completed' && <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg" data-id="element-1267">
          <div className="flex items-start gap-2" data-id="element-1268">
            <CheckCircle size={16} className="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" data-id="element-1269" />
            <div className="text-xs text-green-600 dark:text-green-400" data-id="element-1270">
              <p className="font-medium" data-id="element-1271">
                Successfully fetched{' '}
                {progress.comments_fetched.toLocaleString()} comments!
              </p>
              <p className="text-xs mt-1" data-id="element-1272">
                Charged {progress.credits_charged}{' '}
                {progress.credits_charged === 1 ? 'credit' : 'credits'}
              </p>
            </div>
          </div>
        </div>}
    </div>;
}